import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthorizationHeader, http } from '../config/http';
import { toast } from 'react-toastify';
import { IconArrowRight, IconQrcode, IconRefresh, IconTrash, IconX, IconAdjustmentsHorizontal } from '@tabler/icons-react';
import QRScanner from '../components/QRScanner';
import ReviewModal from '../components/OperasiBarang/ReviewModal';
import HistoryLog from '../components/OperasiBarang/HistoryLog';
import Layouts from '../layouts/Layouts';
import { DinamicTitle } from '../utils/DinamicTitle';

export default function OperasiBarang() {
  DinamicTitle('Operasi Barang || Komaladewi');
  const navigate = useNavigate();

  // State
  const [selectedOperation, setSelectedOperation] = useState(null); // 'transfer' | 'pindah' | 'hapus'
  const [scannedItems, setScannedItems] = useState([]);
  const [historyLog, setHistoryLog] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cabangList, setCabangList] = useState([]);

  // Fetch cabang list
  useEffect(() => {
    async function getCabang() {
      try {
        const res = await http.get('/cabang', {
          headers: { Authorization: getAuthorizationHeader() },
        });
        setCabangList(res.data.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        }
      }
    }
    getCabang();
  }, [navigate]);

  // Handle scan success
  const handleScanSuccess = async (kode) => {
    try {
      const res = await http.get(`/item?kode=${kode}`, {
        headers: { Authorization: getAuthorizationHeader() },
      });

      const foundItems = res.data.data.data;
      if (foundItems && foundItems.length > 0) {
        const item = foundItems[0];

        // Check if item already scanned
        const alreadyScanned = scannedItems.some((si) => si.id === item.id);
        if (alreadyScanned) {
          toast.info(`${item.kode_item} sudah discan sebelumnya`, { autoClose: 1500 });
        } else {
          setScannedItems((prev) => [...prev, item]);
          toast.success(`${item.kode_item} berhasil ditambahkan ✓`, { autoClose: 1500 });
        }
      } else {
        toast.error(`Item dengan kode "${kode}" tidak ditemukan`, { autoClose: 2000 });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Gagal mencari item', { autoClose: 2000 });
      }
    }
  };

  // Remove item from scanned list
  const removeScannedItem = (itemId) => {
    setScannedItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Clear all scanned items
  const clearScannedItems = () => {
    setScannedItems([]);
  };

  // Open review modal
  const openReviewModal = () => {
    if (scannedItems.length === 0) {
      toast.error('Belum ada barang yang discan', { autoClose: 1500 });
      return;
    }
    setShowReviewModal(true);
  };

  // Handle operation confirm
  const handleOperationConfirm = async (formData) => {
    setLoading(true);
    try {
      const ids = scannedItems.map((item) => item.id);
      let response;

      if (selectedOperation === 'transfer') {
        response = await http.post(
          '/item/transfer',
          { cabang_id: formData.cabang_id, items: ids },
          { headers: { Authorization: getAuthorizationHeader() } }
        );
      } else if (selectedOperation === 'pindah') {
        response = await http.post(
          '/item/pindah_etalase',
          { cabang_id: formData.cabang_id, items: ids },
          { headers: { Authorization: getAuthorizationHeader() } }
        );
      } else if (selectedOperation === 'pindah_baki') {
        response = await http.post(
          '/item/pindah_baki',
          { nomor_rak: formData.nomor_rak, items: ids },
          { headers: { Authorization: getAuthorizationHeader() } }
        );
      } else if (selectedOperation === 'hapus') {
        response = await http.post(
          '/item/hapus-multi',
          { items: ids, keterangan: formData.keterangan },
          { headers: { Authorization: getAuthorizationHeader() } }
        );
      }

      // Add to history log
      const newLog = {
        type: selectedOperation,
        itemCount: scannedItems.length,
        timestamp: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }),
        details: selectedOperation === 'hapus' ? formData.keterangan :
          selectedOperation === 'pindah_baki' ? `BAKI: ${formData.nomor_rak}` :
            `Cabang: ${cabangList.find(c => c.id === formData.cabang_id)?.nama_cabang}`
      };
      setHistoryLog((prev) => [newLog, ...prev]);

      toast.success('Operasi berhasil!', { autoClose: 2000 });
      setShowReviewModal(false);
      setScannedItems([]);
    } catch (err) {
      console.error(err);
      toast.error('Gagal melakukan operasi', { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  // Clear history log
  const clearHistoryLog = () => {
    setHistoryLog([]);
  };

  // Reset operation selection
  const resetOperation = () => {
    setSelectedOperation(null);
    setScannedItems([]);
  };

  return (
    <Layouts>
      <div className="p-6">
        {/* Header */}
        {/* <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Operasi Barang</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Scan QR code untuk melakukan operasi barang (transfer, pindah, hapus, baki)
          </p>
        </div> */}

        {/* Operation Selection */}
        {!selectedOperation ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setSelectedOperation('transfer')}
              className="p-6 bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-amber-500 dark:hover:border-amber-500 hover:shadow-lg transition-all group"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-colors">
                  <IconArrowRight className="w-8 h-8 text-amber-600 dark:text-amber-400 group-hover:text-white" />
                </div>
                <span className="font-semibold text-slate-700 dark:text-slate-200">Transfer Cabang</span>
                <span className="text-sm text-slate-500 dark:text-slate-400 text-center">Pindah barang ke cabang lain</span>
              </div>
            </button>

            <button
              onClick={() => setSelectedOperation('pindah')}
              className="p-6 bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all group"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
                  <IconAdjustmentsHorizontal className="w-8 h-8 text-blue-600 dark:text-blue-400 group-hover:text-white" />
                </div>
                <span className="font-semibold text-slate-700 dark:text-slate-200 text-center">Pindah Etalase/Brankas</span>
                <span className="text-sm text-slate-500 dark:text-slate-400 text-center">Pindah lokasi barang</span>
              </div>
            </button>

            <button
              onClick={() => setSelectedOperation('pindah_baki')}
              className="p-6 bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-lg transition-all group"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                  <IconRefresh className="w-8 h-8 text-emerald-600 dark:text-emerald-400 group-hover:text-white" />
                </div>
                <span className="font-semibold text-slate-700 dark:text-slate-200">Pindah BAKI</span>
                <span className="text-sm text-slate-500 dark:text-slate-400 text-center">Ganti nomor baki barang</span>
              </div>
            </button>

            <button
              onClick={() => setSelectedOperation('hapus')}
              className="p-6 bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-red-500 dark:hover:border-red-500 hover:shadow-lg transition-all group"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center group-hover:bg-red-500 group-hover:text-white transition-colors">
                  <IconTrash className="w-8 h-8 text-red-600 dark:text-red-400 group-hover:text-white" />
                </div>
                <span className="font-semibold text-slate-700 dark:text-slate-200">Hapus Barang</span>
                <span className="text-sm text-slate-500 dark:text-slate-400 text-center">Hapus barang dari sistem</span>
              </div>
            </button>
          </div>
        ) : (
          <>
            {/* Operation Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={resetOperation}
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
                >
                  <IconArrowRight className="w-5 h-5 rotate-180" />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                    {selectedOperation === 'transfer' && 'Transfer Cabang'}
                    {selectedOperation === 'pindah' && 'Pindah Etalase/Brankas'}
                    {selectedOperation === 'pindah_baki' && 'Pindah BAKI'}
                    {selectedOperation === 'hapus' && 'Hapus Barang'}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {scannedItems.length} barang discan
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowScanner(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
              >
                <IconQrcode className="w-5 h-5" />
                <span>Scan QR</span>
              </button>
            </div>

            {/* Scanned Items */}
            {scannedItems.length > 0 && (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-sm overflow-hidden mb-4">
                <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700/60 flex items-center justify-between">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    Barang yang Akan Diproses
                  </span>
                  <button
                    onClick={clearScannedItems}
                    className="text-sm text-red-500 hover:text-red-600 font-medium"
                  >
                    Hapus Semua
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                    <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700/60">
                      <tr>
                        <th scope="col" className="px-3 py-2 font-semibold w-12 text-center">No</th>
                        <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Kode</th>
                        <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Barang</th>
                        <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 text-right align-middle">Berat</th>
                        <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 text-center w-20">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {scannedItems.map((item, i) => (
                        <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors bg-white dark:bg-slate-900">
                          <td className="px-3 py-2 text-center text-slate-400 dark:text-slate-500">{i + 1}</td>
                          <td className="px-3 py-2 font-medium border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-700 dark:text-slate-300">{item.kode_item}</td>
                          <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-600 dark:text-slate-400 truncate max-w-[150px]">{item.jenis_kategori?.nama_jenis}</td>
                          <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-right tabular-nums text-slate-700 dark:text-slate-300">{item.berat} gr</td>
                          <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-center">
                            <button
                              onClick={() => removeScannedItem(item.id)}
                              className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              title="Hapus dari list"
                            >
                              <IconX className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setShowScanner(true)}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 dark:disabled:text-slate-400 text-white rounded-xl font-bold transition-colors"
              >
                <IconQrcode className="w-5 h-5" />
                <span>Scan Barang</span>
              </button>
              <button
                onClick={openReviewModal}
                disabled={scannedItems.length === 0 || loading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 dark:disabled:text-slate-400 text-white rounded-xl font-bold transition-colors"
              >
                <IconRefresh className="w-5 h-5" />
                <span>Review & Proses</span>
              </button>
            </div>

            {/* History Log */}
            <HistoryLog history={historyLog} onClear={clearHistoryLog} />
          </>
        )}

        {/* QR Scanner Modal */}
        {showScanner && (
          <QRScanner
            isOpen={showScanner}
            onClose={() => setShowScanner(false)}
            onScanSuccess={handleScanSuccess}
          />
        )}

        {/* Review Modal */}
        {showReviewModal && (
          <ReviewModal
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            operation={selectedOperation}
            items={scannedItems}
            cabangList={cabangList}
            onConfirm={handleOperationConfirm}
            loading={loading}
          />
        )}
      </div>
    </Layouts>
  );
}
